
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { BadgeDollarSign, File } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Enhanced status badge for different states
const getStatusBadge = (status?: string) => {
  switch (status) {
    case "draft":
      return <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200">Draft</Badge>;
    case "sent":
      return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">Sent</Badge>;
    case "approved":
      return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">Approved</Badge>;
    case "overdue":
      return <Badge variant="destructive">Overdue</Badge>;
    case "rejected":
      return <Badge variant="destructive">Rejected</Badge>;
    case "paid":
      return <Badge variant="default" className="bg-green-600">Paid</Badge>;
    case "cancelled":
      return <Badge variant="destructive">Cancelled</Badge>;
    default:
      return status ? <Badge variant="outline">{status}</Badge> : null;
  }
};

interface RelatedEstimatePreviewCardProps {
  estimate: {
    estimate_number?: string;
    title?: string;
    id: string;
    status?: string;
    customer?: { name?: string; last_name?: string };
  };
  compact?: boolean;
  showCustomerInfo?: boolean; // extra control
}

const RelatedEstimatePreviewCard: React.FC<RelatedEstimatePreviewCardProps> = ({
  estimate,
  compact = false,
  showCustomerInfo = true,
}) => {
  if (!estimate || !estimate.id) return null;

  if (compact) {
    // For invoice preview/PDF only
    return (
      <div className="mb-2">
        <span className="font-bold">Related Estimate: </span>
        <Link to={`/estimates/${estimate.id}`} className="text-primary hover:underline">
          {estimate.estimate_number} - {estimate.title}
        </Link>
      </div>
    );
  }

  // Enhanced card for invoice DETAILS page (matches your reference screenshot)
  return (
    <Card className="bg-white border border-blue-100/90 shadow-sm rounded-xl overflow-hidden mb-4">
      <CardHeader className="flex flex-row items-center gap-4 pb-2 px-4 pt-4">
        <BadgeDollarSign className="text-blue-600" size={28} />
        <div>
          <CardTitle className="flex items-center gap-2 text-blue-800 font-semibold text-lg">
            Linked Estimate
            {getStatusBadge(estimate.status)}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-2 flex flex-col gap-2">
        <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
          <div className="flex flex-col">
            <div className="font-bold text-blue-700 text-xl mb-1">
              #{estimate.estimate_number || "Draft"}
            </div>
            {estimate.title && (
              <div className="text-blue-500 text-sm break-words">{estimate.title}</div>
            )}
            {showCustomerInfo && estimate.customer && (
              <div className="text-gray-700 text-sm">
                Customer:{" "}
                <span className="font-medium">
                  {estimate.customer.name} {estimate.customer.last_name}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RelatedEstimatePreviewCard;
